import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { getAuth, getDb } from '../firebase';
import { detachUserFromFamily } from './familyService';
import { buildSignupNotificationPrefs } from '../utils/notificationPrefs';

const buildNewUserDoc = (user, displayName, signupPlatform = null, attribution = null, foundVia = null) => {
  const base = {
    email: user.email,
    displayName: displayName || user.displayName,
    photoURL: user.photoURL || null,
    familyIds: [],
    createdAt: new Date().toISOString(),
    signupPlatform: signupPlatform || null,
    platforms: signupPlatform ? { [signupPlatform]: true } : {},
    // Canonical signup defaults — see buildSignupNotificationPrefs(). Includes
    // handoffReminder: false (opt-in; the only proactive push, so it stays off
    // until the user turns it on in Settings, and the Cloud Function's `=== true`
    // gate is the real guard regardless of what is stored here).
    notificationPrefs: buildSignupNotificationPrefs()
  };

  // Attribution is optional — only stamp when we actually captured a source.
  if (attribution && (attribution.utm_source || attribution.source)) {
    base.signupSource = attribution.source || attribution.utm_source || null;
    base.signupMedium = attribution.utm_medium || null;
    base.signupCampaign = attribution.utm_campaign || null;
    base.signupContent = attribution.utm_content || null;
    base.signupReferrer = attribution.referrer || null;
    base.signupFirstTouchAt = attribution.ts ? new Date(attribution.ts).toISOString() : null;
  }

  // Self-reported "how did you find us" answer from the signup screen. Optional,
  // so only stamped when the user actually picked something.
  if (foundVia) base.foundVia = foundVia;

  return base;
};

export const registerUser = async (email, password, displayName, signupPlatform = null, attribution = null, foundVia = null) => {
  const auth = getAuth();
  const db = getDb();
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  await updateProfile(user, { displayName });

  const writePromise = setDoc(doc(db, 'users', user.uid), buildNewUserDoc(user, displayName, signupPlatform, attribution, foundVia));

  try {
    await Promise.race([
      writePromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore write timeout')), 5000))
    ]);
  } catch (firestoreError) {
    console.error('Firestore user doc write failed or timed out:', firestoreError);
  }

  return user;
};

export const loginUser = async (email, password) => {
  const auth = getAuth();
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

export const logoutUser = async () => {
  const auth = getAuth();
  await signOut(auth);
};

export const getUserData = async (userId) => {
  const db = getDb();
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

export const subscribeToUserData = (userId, callback) => {
  const db = getDb();
  return onSnapshot(
    doc(db, 'users', userId),
    (snap) => {
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    },
    (err) => {
      console.error('subscribeToUserData error:', err);
      callback(null);
    }
  );
};

/**
 * Merge-write the first-run tutorial state onto the user's document.
 *
 * `patch` is shallow-merged into `users/{uid}.tutorial` (e.g. `{ lastStep: 2 }`
 * or `{ completed: true, completedAt }`). The live `subscribeToUserData`
 * subscription (see AuthContext) delivers the change back into `userData.tutorial`,
 * which is what the TutorialOverlay gates on — so callers never need to refetch.
 *
 * Uses a dotted field path so a partial patch never clobbers sibling keys.
 */
export const updateUserTutorial = async (userId, patch) => {
  if (!userId || !patch) return;
  const db = getDb();
  const updates = {};
  Object.entries(patch).forEach(([key, value]) => {
    updates[`tutorial.${key}`] = value;
  });
  try {
    await updateDoc(doc(db, 'users', userId), updates);
  } catch (error) {
    // A dotted update creates a missing `tutorial` map on its own, so the only
    // way this throws is that the user doc itself doesn't exist yet — a
    // brand-new account whose signup profile write hasn't landed. Do NOT
    // setDoc(merge) a fallback here: that would conjure a bare users/{uid} doc
    // with no createdAt/email, which makes the OAuth signup handler skip its
    // profile write and the onboarding-email trigger skip its send (see
    // functions/metrics/healUserProfile.js). Skip instead — tutorial state
    // re-saves on the next step once the profile doc exists.
    console.warn('updateUserTutorial skipped (profile doc not ready yet):', error?.message || error);
  }
};

/**
 * Merge-write the App Store review-prompt state onto the user's document.
 *
 * Mirrors `updateUserTutorial`: `patch` is shallow-merged into
 * `users/{uid}.reviewPrompt` (e.g. `{ successCount: 3 }` or
 * `{ promptCount, lastPromptedAt }`). The live `subscribeToUserData` subscription
 * delivers the change back into `userData.reviewPrompt`, which the gating hook
 * (`useReviewPrompt`) reads — so callers never refetch. Dotted field paths keep a
 * partial patch from clobbering sibling keys. Values are stored verbatim (numbers,
 * booleans, or ISO strings for timestamps).
 */
export const updateUserReviewPrompt = async (userId, patch) => {
  if (!userId || !patch) return;
  const db = getDb();
  const updates = {};
  Object.entries(patch).forEach(([key, value]) => {
    updates[`reviewPrompt.${key}`] = value;
  });
  try {
    await updateDoc(doc(db, 'users', userId), updates);
  } catch (error) {
    // Same reasoning as updateUserTutorial: the only failure mode is a
    // not-yet-created profile doc, and creating a stub here would strip the
    // user of createdAt/email. Skip rather than conjure the doc.
    console.warn('updateUserReviewPrompt skipped (profile doc not ready yet):', error?.message || error);
  }
};

/**
 * Permanently delete the current user's account.
 *
 * Caller MUST have re-authenticated the user within the last few minutes —
 * Firebase rejects `user.delete()` without a recent credential. Platform code
 * handles re-auth (see mobile DeleteAccountScreen and web DeleteAccountPage).
 *
 * Order matters:
 *  1. Detach from every family first so no family is left with a dangling
 *     parent reference. Errors here are swallowed — we still want to attempt
 *     the final auth deletion.
 *  2. Delete the Firestore `users/{uid}` doc.
 *  3. Delete the Firebase Auth user.
 */
export const deleteCurrentUser = async () => {
  const auth = getAuth();
  const db = getDb();
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const userDocRef = doc(db, 'users', user.uid);
  let familyIds = [];
  try {
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      familyIds = userDoc.data().familyIds || [];
    }
  } catch (err) {
    console.warn('Failed to read user doc before deletion', err);
  }

  for (const familyId of familyIds) {
    try {
      await detachUserFromFamily(familyId, user.uid);
    } catch (err) {
      console.error('Failed to detach user from family', familyId, err);
    }
  }

  try {
    await deleteDoc(userDocRef);
  } catch (err) {
    console.warn('Failed to delete user doc', err);
  }

  await user.delete();
};

export { buildNewUserDoc };
